import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Mountain, Users, Calendar, FolderOpen, 
  BookOpen, Images, Bed, MessageSquare, Heart, Settings,
  LogOut, Menu, X, Search, Trash2, Copy, Check, Filter
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { ImageUpload, ImagePreview } from '../ImageUpload';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SIDEBAR_ITEMS = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/admin' },
  { icon: Mountain, label: 'Falesie', path: '/admin/crags' },
  { icon: Users, label: 'Team', path: '/admin/team' },
  { icon: Calendar, label: 'Eventi', path: '/admin/events' },
  { icon: FolderOpen, label: 'Progetti', path: '/admin/projects' },
  { icon: BookOpen, label: 'Blog', path: '/admin/blog' },
  { icon: Images, label: 'Gallery', path: '/admin/gallery' },
  { icon: Bed, label: 'Alloggi', path: '/admin/accommodations' },
  { icon: MessageSquare, label: 'Messaggi', path: '/admin/messages' },
  { icon: Heart, label: 'Donazioni', path: '/admin/donations' },
  { icon: Search, label: 'SEO', path: '/admin/seo' },
  { icon: Images, label: 'Uploads', path: '/admin/uploads' },
  { icon: Settings, label: 'Impostazioni', path: '/admin/settings' },
];

const CATEGORIES = [
  { value: 'all', label: 'Tutte le categorie' },
  { value: 'crags', label: 'Falesie' },
  { value: 'team', label: 'Team' },
  { value: 'events', label: 'Eventi' },
  { value: 'blog', label: 'Blog' },
  { value: 'gallery', label: 'Gallery' },
  { value: 'other', label: 'Altro' },
];

export function AdminUploads() {
  const { user, logout, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [uploadCategory, setUploadCategory] = useState('');
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/admin/login');
      return;
    }
    fetchUploads();
  }, [isAuthenticated, authLoading, navigate, selectedCategory]);

  const fetchUploads = async () => {
    try {
      const url = selectedCategory && selectedCategory !== 'all'
        ? `${API}/uploads?category=${selectedCategory}`
        : `${API}/uploads`;
      const res = await axios.get(url);
      setUploads(res.data);
    } catch (error) {
      console.error('Error fetching uploads:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = (uploaded) => {
    const newUploads = Array.isArray(uploaded) ? uploaded : [uploaded];
    setUploads(prev => [...newUploads, ...prev]);
  };

  const handleDelete = async (image) => {
    try {
      await axios.delete(`${API}/uploads/${image.id}`);
      setUploads(prev => prev.filter(u => u.id !== image.id));
      toast.success('Immagine eliminata');
    } catch (error) {
      toast.error('Errore durante l\'eliminazione');
    }
  };

  const copyToClipboard = (url) => {
    const fullUrl = url.startsWith('/api') 
      ? `${process.env.REACT_APP_BACKEND_URL}${url}`
      : url;
    navigator.clipboard.writeText(fullUrl);
    setCopiedId(url);
    toast.success('URL copiato');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-uploads-page">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#0A0A0A] transform transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex items-center justify-between h-16 px-6 border-b border-gray-800">
          <Link to="/" className="text-white font-heading text-lg uppercase">CMS Admin</Link>
          <button className="lg:hidden text-white" onClick={() => setSidebarOpen(false)}>
            <X className="w-6 h-6" />
          </button>
        </div>
        <nav className="p-4 space-y-1 max-h-[calc(100vh-8rem)] overflow-y-auto">
          {SIDEBAR_ITEMS.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                item.path === '/admin/uploads' ? 'bg-[#FF6D00] text-white' : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <item.icon className="w-5 h-5" />
              <span className="text-sm">{item.label}</span>
            </Link>
          ))}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-800">
          <button onClick={logout} className="flex items-center gap-3 px-4 py-3 text-gray-400 hover:text-white w-full">
            <LogOut className="w-5 h-5" />
            <span className="text-sm">Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:ml-64">
        <header className="bg-white border-b h-16 flex items-center justify-between px-6">
          <button className="lg:hidden" onClick={() => setSidebarOpen(true)}>
            <Menu className="w-6 h-6" />
          </button>
          <h1 className="font-heading text-xl uppercase">Gestione Immagini</h1>
          <Link to="/" className="text-sm text-[#FF6D00] hover:underline">Vedi sito</Link>
        </header>

        <main className="p-6">
          {/* Upload Section */}
          <div className="bg-white rounded-lg border p-6 mb-6">
            <h2 className="font-heading uppercase mb-4">Carica nuove immagini</h2>
            <div className="flex gap-4 items-end mb-4">
              <div className="flex-grow">
                <label className="block text-sm font-medium mb-2">Categoria</label>
                <Select value={uploadCategory} onValueChange={setUploadCategory}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.filter(c => c.value !== 'all').map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <ImageUpload 
              onUpload={handleUpload}
              category={uploadCategory}
              multiple={true}
            />
          </div>

          {/* Filter */}
          <div className="flex gap-4 items-center mb-6">
            <Filter className="w-5 h-5 text-gray-400" />
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-64">
                <SelectValue placeholder="Filtra per categoria" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-gray-500 text-sm">
              {uploads.length} immagini
            </span>
          </div>

          {/* Uploads Grid */}
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin w-8 h-8 border-4 border-[#FF6D00] border-t-transparent rounded-full" />
            </div>
          ) : uploads.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-lg border">
              <Images className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Nessuna immagine caricata</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {uploads.map((upload) => (
                <div key={upload.id} className="bg-white rounded-lg border overflow-hidden group">
                  <div className="aspect-square relative">
                    <img 
                      src={upload.url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${upload.url}` : upload.url}
                      alt={upload.original_filename}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <button
                        onClick={() => copyToClipboard(upload.url)}
                        className="w-10 h-10 bg-white rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors"
                        title="Copia URL"
                      >
                        {copiedId === upload.url ? (
                          <Check className="w-5 h-5 text-green-600" />
                        ) : (
                          <Copy className="w-5 h-5" />
                        )}
                      </button>
                      <button
                        onClick={() => handleDelete(upload)}
                        className="w-10 h-10 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                        title="Elimina"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                  <div className="p-3">
                    <p className="text-xs text-gray-600 truncate" title={upload.original_filename}>
                      {upload.original_filename}
                    </p>
                    <div className="flex justify-between items-center mt-1">
                      <span className="text-xs text-gray-400">
                        {formatFileSize(upload.size)}
                      </span>
                      {upload.category && (
                        <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                          {upload.category}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
